import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronRight, FlaskConical, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { ROUTES } from '@/app/routes';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ContentInfrastructureHealth } from '@/features/content/components/ContentInfrastructureHealth';
import { useCreateLabCase, useDeleteLabCase, useLabCases } from '../labApi';

export function PipelineLabListPage() {
  const navigate = useNavigate();
  const { data: cases, isLoading, isError } = useLabCases();
  const createCase = useCreateLabCase();
  const deleteCase = useDeleteLabCase();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const onCreate = async () => {
    if (!name.trim()) {
      toast.error('Name is required');
      return;
    }
    try {
      const created = await createCase.mutateAsync({
        name: name.trim(),
        ...(description.trim() ? { description: description.trim() } : {}),
      });
      setName('');
      setDescription('');
      toast.success('Test case created');
      void navigate(ROUTES.pipelineLabCase(String(created.id)));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to create case');
    }
  };

  return (
    <div className="flex flex-col gap-6 p-6">
      <nav className="flex items-center gap-1 text-sm text-muted-foreground">
        <Link to={ROUTES.moderation} className="hover:text-foreground">
          Moderation
        </Link>
        <ChevronRight className="size-4" />
        <span className="text-foreground">Pipeline Lab</span>
      </nav>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
            <FlaskConical className="size-6" />
            Pipeline Lab
          </h1>
          <p className="mt-1 max-w-xl text-sm text-muted-foreground">
            Reusable caption + media fixtures. Trigger a run to compare deep GPU and Cloud
            results side by side — no tipmi posts are created.
          </p>
        </div>
        <ContentInfrastructureHealth className="h-auto bg-zinc-950 text-zinc-100 shadow-none" />
      </div>

      <section className="rounded-lg border bg-card p-4">
        <h2 className="mb-3 text-sm font-medium">New test case</h2>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <label className="flex flex-1 flex-col gap-1 text-sm">
            Name
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Caption NSFW regression"
            />
          </label>
          <label className="flex flex-[2] flex-col gap-1 text-sm">
            Description
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional notes"
            />
          </label>
          <Button onClick={() => void onCreate()} disabled={createCase.isPending}>
            <Plus className="size-4" />
            Create
          </Button>
        </div>
      </section>

      <section className="rounded-lg border bg-card">
        <div className="border-b px-4 py-3 text-sm font-medium">Test cases</div>
        {isLoading ? (
          <p className="p-4 text-sm text-muted-foreground">Loading…</p>
        ) : isError ? (
          <p className="p-4 text-sm text-destructive">Failed to load cases</p>
        ) : !cases?.length ? (
          <p className="p-4 text-sm text-muted-foreground">No test cases yet</p>
        ) : (
          <ul className="divide-y">
            {cases.map((c) => (
              <li
                key={c.id}
                className="flex items-center justify-between gap-3 px-4 py-3 hover:bg-muted/40"
              >
                <button
                  type="button"
                  className="min-w-0 flex-1 text-left"
                  onClick={() => void navigate(ROUTES.pipelineLabCase(String(c.id)))}
                >
                  <div className="truncate font-medium">{c.name}</div>
                  <div className="truncate text-xs text-muted-foreground">
                    {c.fixtureCount ?? 0} fixture{(c.fixtureCount ?? 0) === 1 ? '' : 's'}
                    {c.description ? ` · ${c.description}` : ''}
                  </div>
                </button>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Delete ${c.name}`}
                  onClick={() => {
                    if (
                      !window.confirm(
                        `Delete “${c.name}” and its uploaded fixture media?`,
                      )
                    )
                      return;
                    void deleteCase
                      .mutateAsync(c.id)
                      .then(() => toast.success('Deleted'))
                      .catch((err: unknown) =>
                        toast.error(err instanceof Error ? err.message : 'Delete failed'),
                      );
                  }}
                >
                  <Trash2 className="size-4" />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
